import { Injectable, OnDestroy } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import 'lodash';
import { BehaviorSubject, EMPTY, ReplaySubject } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { OriginRestAPIService } from 'app/services/origin-rest-api.service';
import { CommonService } from 'app/shared/services/common.service';
import { RestAPIService } from 'app/services/rest-api.service';
import { AssetsService } from './assets.service';
import { DatasourceInterface, DsNodeTagInterface, GetLinkedDatasourceNodesInterface, GetEudataInterface, TagDetail, DatasourceNodesInterface } from 'app/services/graphqlApi.service';
import { LinkedDatasourceNode, Datasource, DatasourceNode, DsNodeTags, EntityNodeLink, TagMappingLinkPayload, IUnlinkTagMappingPayload } from '../interfaces/interfaces';
import { getDatasourceById, getDatasourceNodesByAssetId, getDatasources, getEUs, getLinkedTagsByAssetId, getLinkedTagsByNodeId, getQualityCodes } from 'app/shared/constants/gql';
import { RestRequestType } from 'app/shared/enums/rest-request-type.enum';

declare var _: any;

export interface EuData {
  id: string;
  name: string;
  quantityType: string;
  catalogSymbol: string;
  baseUnit: string;
  createdBy: string;
  createdOn: string;
  rP66Symbol?: string;
}

export interface DsTagValues {
  uuid: string;
  name: string;
  q: string;
  t: string;
  uom: string;
  v: string;
}

@Injectable({
  providedIn: 'root'
})

export class LinkAssetServiceService implements OnDestroy {
  readonly destroyed = new ReplaySubject(1);
  private _selectedDatasourceNode = new BehaviorSubject<LinkedDatasourceNode>(null);
  readonly selectedDatasourceNode$ = this._selectedDatasourceNode.asObservable();

  private _measurmentCategories = new BehaviorSubject<EuData[]>([]);
  readonly measurmentCategories$ = this._measurmentCategories.asObservable();

  private _measurmentCategory = new BehaviorSubject<{ tag: string, category: string }>(null);
  readonly measurmentCategory$ = this._measurmentCategory.asObservable();

  private _selectedTag = new BehaviorSubject<TagDetail>(null);
  readonly selectedTag$ = this._selectedTag.asObservable();

  private _datasorceNodes = new BehaviorSubject<LinkedDatasourceNode[]>([]);
  readonly datasorceNodes$ = this._datasorceNodes.asObservable();

  private _datasorces = new BehaviorSubject<Datasource[]>([]);
  readonly datasorces$ = this._datasorces.asObservable();

  private _nodes = new BehaviorSubject<DatasourceNode[]>([]);
  readonly nodes$ = this._nodes.asObservable();

  public selectedNodeTags = new BehaviorSubject<TagDetail[]>([]);
  readonly selectedNodeTags$ = this.selectedNodeTags.asObservable();

  private _dsNodeTags = new BehaviorSubject<DsNodeTags[]>([]);
  readonly dsNodeTags$ = this._dsNodeTags.asObservable();

  readonly checkedLinkTags = new BehaviorSubject<Map<string, TagDetail>>(new Map());
  readonly checkedLinkTags$ = this.checkedLinkTags.asObservable();

  readonly _selectedDSTag = new BehaviorSubject<TagDetail>(null);
  readonly selectedDSTag$ = this._selectedDSTag.asObservable();

  readonly prohibitedWords: string[] = ['node info/', 'node control/', 'templates/', 'devices/', 'bdseq/', 'device info/', 'device control/',
    'node temperature', 'devices', 'node info', 'device info', 'device temperature', 'bdseq'];

  constructor(
    private apiService: RestAPIService,
    private toastr: ToastrService,
    readonly assetsService: AssetsService,
    private commonService: CommonService) {

    this.selectedDatasourceNode$
      .pipe(filter(node => !!node?.datasourceNodeId), switchMap(node => this.getDsNodeTags(node)))
      .subscribe(dsTags => {
        console.log('dsTags:', dsTags);
        const selectedNode: LinkedDatasourceNode = this._selectedDatasourceNode.getValue();
        const allDsTags: DsNodeTags[] = dsTags.filter(dsTag => !!dsTag.name && (dsTag.groupName === selectedNode?.groupName)) || [];
        console.log('filteredDsTags:', allDsTags);
        this._dsNodeTags.next(allDsTags);
      });
  }

  addCheckedTagValue(tag: TagDetail) {
    const tagMap = this.checkedLinkTags.getValue();
    const id = tag.id + '_' + tag.componentInstanceId;
    tagMap.set(id, tag);
    this.checkedLinkTags.next(tagMap);
  }

  removeCheckedTagValue(tag: TagDetail) {
    const tagMap = this.checkedLinkTags.getValue();
    const id = tag.id + '_' + tag.componentInstanceId;
    tagMap.delete(id);
    this.checkedLinkTags.next(tagMap);
  }

  setDatasourceNode(node: LinkedDatasourceNode) {
    this._selectedDatasourceNode.next(node);
  }

  setMeasurmentcategory(tagCategory: { tag: string, category: string }) {
    this._measurmentCategory.next(tagCategory);
  }

  setSelectedTag(tag: TagDetail) {
    this._selectedTag.next(tag);
  }

  setAssetTagsByNode() {
    this.assetsService.tags$.pipe(take(1)).subscribe(tags => {
      this.selectedNodeTags.next([]);
      this.mapLinkedTags(tags);
    });
  }

  mapLinkedTags(tags: TagDetail[]) {
    const node = this._selectedDatasourceNode.getValue();
    this.assetsService.getTagMappingLinks(node.entityId).pipe(
      take(1),
      withLatestFrom(this.selectedDatasourceNode$),
      map(([linkedTagMap, node]) => {
        const mappedTags = [];
        const selectedNodeLinkedTags = [];
        for (let tag of tags) {
          const compInstTagId = tag.id + '_' + tag.componentInstanceId;
          const lTag = linkedTagMap.get(compInstTagId)!;
          if (linkedTagMap.has(compInstTagId)) {
            if (lTag.entityDsNodeLinksId === node.id) {
              mappedTags.push({ ...tag, ...lTag });
              selectedNodeLinkedTags.push(lTag);
            } else if (!lTag.isLinked) {
              mappedTags.push({ ...tag });
            }
          } else {
            mappedTags.push({ ...tag });
          }
        }

        return [mappedTags, selectedNodeLinkedTags];
      })
    ).subscribe(([tags, selectedNodeLinkedTags]) => {
      this.selectedNodeTags.next([...tags]);
      if (selectedNodeLinkedTags.length > 0) {
        this.setAssetTagsRealTimeValue(selectedNodeLinkedTags);
      }
    });
  }

  refreshDsTagValues() {
    const tags = this.selectedNodeTags.getValue().filter(t => t.dsNodeId);
    if (tags.length > 0) {
      this.setAssetTagsRealTimeValue(tags);
    }
  }

  setAssetTagsRealTimeValue(tags: TagDetail[]) {
    const node = this._selectedDatasourceNode.getValue();
    const tagsSummary = tags.map(t => ({ dsNodeTagId: t.dsNodeTagId, dsNodeTagName: t.dsNodeTagName }));
    const payload = {
      dsNodeName: node.datasourceNodeName,
      tags: tagsSummary,
    }

    this.commonService.getDSNodeTagValues(payload, node.datasourceNodeId)
      .pipe(map(tagValues => tagValues.tags))
      .subscribe(tagValues => {
        this.updateDSTagsList(tagValues);
      });
  }

  private updateDSTagsList(tagValues: DsTagValues[]) {
    const allTags = this.selectedNodeTags.getValue();
      const valuesMap = tagValues.reduce((dsTagValueMap, tagValue) => {
      dsTagValueMap.set(tagValue.uuid, tagValue);
      return dsTagValueMap;
    }, new Map());

    for (let tag of allTags) {
      if (valuesMap.has(tag.dsNodeTagId)) {
        const tagValue = valuesMap.get(tag.dsNodeTagId);
        tag.dsTagValue = tagValue.v;
        tag.dsTagTimestamp = tagValue.t;
        tag.dsTagQuality = tagValue.q;
        tag.dsTagUom = tagValue.uom;
      }
    }

    this.selectedNodeTags.next(allTags);
  }

  updateSingleTagList(tag: TagDetail) {
    const selectedNodeTags = this.selectedNodeTags.getValue();
    const index = selectedNodeTags.findIndex(t => t.id === tag.id);
    if (index !== -1) {
      selectedNodeTags[index] = tag;
      this.selectedNodeTags.next(selectedNodeTags);
    }
  }

  setAllNodeTags(nodeTags: TagDetail[]) {
    this.selectedNodeTags.next(nodeTags);
  }

  getEu() {
    return this.apiService.getGQLData({ query: getEUs.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: GetEudataInterface[] = response.data.intellija_ref_eu;
        return queryResponse.map(record => {
          return {
            id: record.id,
            name: record.name,
            quantityType: record.quantity_type,
            catalogSymbol: record.catalog_symbol,
            baseUnit: record.base_unit,
            createdBy: record.created_by,
            createdOn: record.created_date,
            rP66Symbol: record.rp66_symbol,
          };
        });
      }));
  }

  setMeasurmentCategories() {
    this.getEu().pipe(takeUntil(this.destroyed)).subscribe(eu => {
      const category = _.uniqBy(eu, 'quantityType');
      this._measurmentCategories.next(category);
    })
  }

  getLinkedNodes(id: string) {
    return this.apiService.getGQLData({ query: getDatasourceNodesByAssetId.query, params: { assetId: id } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: GetLinkedDatasourceNodesInterface[] = response.data?.intellija_entity_ds_node_links;
        const list: LinkedDatasourceNode[] = (queryResponse || [])
          .map(record => ({
            id: record.id,
            entityId: record.entity_id,
            effectiveFrom: record.effective_from,
            effectiveTo: record.effective_to,
            datasourceNodeId: record.ds_node_id,
            datasourceNodeName: record.ds_node?.name,
            datasourecNodeTypeName: record.ds_node?.ref_ds_node_type?.name,
            datasourceNodeTypeId: record.ds_node?.ref_ds_node_type?.id,
            datasourceName: record.ds_node?.datasource?.name,
            datasourceId: record.ds_node?.datasource?.id,
            datasourceTypeName: record.ds_node?.datasource?.ref_datasource_type?.name,
            datasourceTypeId: record.ds_node?.datasource?.ref_datasource_type?.id,
            groupName: record.ds_node?.groupName,
            isLinked: record.is_linked,
            linkedCount: record.tag_mapping_links_aggregate?.aggregate?.count,
            isDeleted: record.is_deleted || false,
          }));

        return list;
      }));
  }

  setLinkedNodes(id) {
    this.getLinkedNodes(id).pipe(takeUntil(this.destroyed)).subscribe((data) => {
      console.log('data: ', data);
      this._datasorceNodes.next(data);
    });
  }

  setDatasources() {
    if (this._datasorces.getValue()?.length > 0) return;
      this.apiService.getGQLData({ query: getDatasources.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: DatasourceInterface[] = response.data?.intellija_datasources;
        return (queryResponse || [])
          .map(record => ({
            id: record.id,
            name: record.name,
            typeName: record.ref_datasource_type?.name,
            typeId: record.ref_datasource_type?.id,
          }));
      }), take(1)).subscribe(ds => {
        this._datasorces.next(ds);
      });
  }

  setDatasourceNodes(id: string) {
    this.apiService.getGQLData({ query: getDatasourceById.query, params: { id: id } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: DatasourceNodesInterface[] = response.data?.intellija_ds_nodes;
        return (queryResponse || [])
          .map(record => ({
            id: record.id,
            datasourceId: record.ds_id,
            name: record.name,
            typeName: record.ref_ds_node_type?.name,
            typeId: record.ref_ds_node_type?.id,
            groupName: record.config?.gn
          }));
      }), take(1)).subscribe(ds => {
        this._nodes.next(ds);
      });
  }

  getDsNodeTags(node: LinkedDatasourceNode) {
    return this.apiService.getGQLData({ query: getLinkedTagsByNodeId.query, params: { nodeId: node.datasourceNodeId } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: DsNodeTagInterface[] = response.data?.intellija_ds_node_tags;
        return (queryResponse || [])
          .map(tag => ({
            dsNodeId: tag.ds_node_id,
            dsNodeName: node.datasourceNodeName,
            name: tag.name,
            id: tag.id,
            deviceName: tag.deviceName,
            groupName: tag.groupName
          }));
      }));
  }

  addEntityNodeLink(node: EntityNodeLink) {
    return this.apiService.post('entity-dsnode-links', RestRequestType.CREATE_NODELINK, node)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Entity node link created successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  updateEntityNodeLink(node: LinkedDatasourceNode, isLink: boolean) {
    const linkUpdateUrl = isLink ? `entity-dsnode-links/${node.id}/unlink?comment_code=${node.commentCode}` : `entity-dsnode-links/${node.id}/link`;
    return this.apiService.post(linkUpdateUrl, RestRequestType.UPDATE_NODELINK)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            const message = isLink ? 'Entity node unlinked successfully!' : 'Entity node linked successfully!';
            this.toastr.success(message);
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  deleteEntityNodeLink(id: string) {
    return this.apiService.delete(`entity-dsnode-links/${id}`)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          this.toastr.success('Entity node link deleted successfully!');
        }));
  }

  linkAssetDsTags(payload: TagMappingLinkPayload[]) {
    return this.apiService.post('tag-mapping-links', RestRequestType.CREATE_ASSET_DS_LINK, { linkList: payload })
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Entity and node tag linked successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  updateAssetDsTagLinkState(payload: TagMappingLinkPayload[]) {
    return this.apiService.post('tag-mapping-links/link', RestRequestType.CREATE_ASSET_DS_LINK, { linkList: payload })
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Entity and node tag linked successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  unLinkAssetDsTags(payload: IUnlinkTagMappingPayload) {
    console.log('payload: Final', payload);
    return this.apiService.post(`tag-mapping-links/unlink`, RestRequestType.CREATE_ASSET_DS_LINK, payload)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Entity and node tag unlinked successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  getQualityCodes(){
    return this.apiService.getGQLData({ query: getQualityCodes.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response: any) =>
        (response?.data?.intellija_ref_quality_code || [])
      ));
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }
}